/**
 * Настройки подключения 1С — схема владельца вида
 * `Platform.Infrastructure/Connectors/onec.settings.schema.json`
 * (`urn:kalmykov:onec:settings:v1`). Проверка здесь — подсказка до отправки;
 * окончательно схему применяет Platform. Пароля в настройках нет: он —
 * секрет ресурса и уходит отдельным полем команды.
 */
export const ONEC_KIND = 'onec'
export const ONEC_TITLE = '1С:Предприятие'
export const ONEC_SCHEMA_VERSION = 'onec.settings.v1'

/** Права вида словами для реестра ресурсов (код показывается рядом). */
export const ONEC_CAPABILITY_LABELS: Readonly<Record<string, string>> = {
  'catalog.read': 'Чтение товаров',
  'catalog.write': 'Правка товаров',
  'catalog.refresh': 'Обновление каталога',
  'prices.read': 'Чтение цен',
  'prices.write': 'Правка цен',
  'stock.read': 'Чтение остатков',
  'supplies.read': 'Чтение поставок',
  'supplies.write': 'Правка поставок',
  'supplies.post': 'Проведение поставок',
  'import.read': 'Чтение импорта накладных',
  'import.write': 'Импорт накладных',
  'inventory.read': 'Чтение инвентаризации',
  'inventory.write': 'Проведение инвентаризации',
  'receipts.read': 'Чтение чеков',
  'diagnostics.read': 'Диагностика базы',
}

/** Что право открывает человеку (п. 10 замечаний 13.10.2026): одна фраза рядом с названием. */
export const ONEC_CAPABILITY_HINTS: Readonly<Record<string, string>> = {
  'catalog.read': 'Видеть номенклатуру базы: поиск, карточки товаров и их фото.',
  'catalog.write': 'Создавать и править карточки номенклатуры в 1С, в том числе по правилам обмена.',
  'catalog.refresh': 'Перечитать номенклатуру и цены из 1С целиком, не дожидаясь изменений.',
  'prices.read': 'Видеть прайс по видам цен, цены и остатки товаров базы.',
  'prices.write': 'Записывать цены в регистр цен 1С.',
  'stock.read': 'Видеть остатки товаров по складам базы.',
  'supplies.read': 'Видеть поставки, приходные накладные 1С и склады базы.',
  'supplies.write': 'Заводить и править черновики поставок.',
  'supplies.post': 'Проводить поставку документом в 1С.',
  'import.read': 'Видеть разбор загруженных накладных и профили импорта.',
  'import.write': 'Загружать накладные и сохранять профили импорта.',
  'inventory.read': 'Видеть инвентаризацию, ценники и цены склада по штрихкоду.',
  'inventory.write': 'Вести и проводить инвентаризацию в 1С.',
  'receipts.read': 'Видеть кассы ККМ, чеки и возвраты базы 1С за период.',
  'diagnostics.read': 'Видеть профиль базы и проверять связь с 1С.',
}

export const ONEC_PROFILES = [
  { value: 'small-business', label: 'Управление нашей фирмой' },
  { value: 'retail', label: 'Розница' },
] as const

/** Ключи шаблона создания товара: ссылки 1С (GUID, не нулевой). */
export const ONEC_TEMPLATE_KEYS = [
  { key: 'storageAccountKey', label: 'Счёт учёта запасов' },
  { key: 'costAccountKey', label: 'Счёт затрат' },
  { key: 'incomeAccountKey', label: 'Счёт доходов' },
  { key: 'vatInAccountKey', label: 'Счёт НДС входящего' },
  { key: 'vatOutAccountKey', label: 'Счёт НДС исходящего' },
  { key: 'categoryKey', label: 'Категория номенклатуры' },
  { key: 'directionKey', label: 'Направление деятельности' },
  { key: 'defaultUnitKey', label: 'Единица измерения по умолчанию' },
] as const

/**
 * Умолчания создания карточки (RP-FB71, шаг 4.3): служебные реквизиты новой карточки номенклатуры, которые не берутся из общего товара, —
 * имена элементов перечислений 1С. Не заполнено — действует значение по умолчанию службы (подсказка у поля).
 */
export const ONEC_CREATION_DEFAULTS = [
  { key: 'vatKind', label: 'Вид ставки НДС новой карточки', hint: 'Если пусто — «БезНДС»' },
  { key: 'itemType', label: 'Тип номенклатуры новой карточки', hint: 'Если пусто — «Запас»' },
  { key: 'paymentSubject', label: 'Признак предмета расчёта новой карточки', hint: 'Если пусто — «Товар»' },
  { key: 'valuationMethod', label: 'Метод оценки новой карточки', hint: 'Если пусто — «ПоСредней»' },
  { key: 'replenishmentMethod', label: 'Способ пополнения новой карточки', hint: 'Если пусто — «Закупка»' },
] as const

/** Наибольшая длина значения умолчания (как в схеме `creationDefault`). */
const DEFAULT_MAX_LENGTH = 100

export interface OneCSettingsIssue {
  field: string
  message: string
}

/** Предельная длина текстовых полей настроек и адреса OData. */
const MAX_TEXT_LENGTH = 200
const MAX_BASE_URL_LENGTH = 2048

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

export function emptyOneCSettings(): Record<string, unknown> {
  return {
    schemaVersion: ONEC_SCHEMA_VERSION,
    baseUrl: '',
    infobase: '',
    username: '',
    configurationProfile: 'small-business',
  }
}

/**
 * Адрес в каноническом виде, как его сравнивает Platform (`ResourceUrlRule`:
 * значение обязано совпасть с `Uri.AbsoluteUri`): хост строчными, `/` после
 * хоста, кириллица в пути — percent-кодом. Не адрес — `null`.
 */
export function canonicalUrl(value: string): string | null {
  try {
    return new URL(value.trim()).href
  } catch {
    return null
  }
}

export function stringField(settings: Readonly<Record<string, unknown>>, name: string): string {
  const value = settings[name]
  return typeof value === 'string' ? value : ''
}

function checkText(
  issues: OneCSettingsIssue[],
  settings: Readonly<Record<string, unknown>>,
  field: string,
  title: string,
): void {
  const value = stringField(settings, field)
  if (value.trim() === '') issues.push({ field, message: `${title}: обязательно` })
  else if (value.length > MAX_TEXT_LENGTH) issues.push({ field, message: `${title}: не длиннее ${String(MAX_TEXT_LENGTH)} символов` })
}

export function validateOneCSettings(settings: Readonly<Record<string, unknown>>): OneCSettingsIssue[] {
  const issues: OneCSettingsIssue[] = []
  const baseUrl = stringField(settings, 'baseUrl').trim()
  if (baseUrl === '') issues.push({ field: 'baseUrl', message: 'Адрес OData: обязательно' })
  else if (!/^https:\/\/[^\s/?#]+/i.test(baseUrl) || baseUrl.length > MAX_BASE_URL_LENGTH)
    issues.push({ field: 'baseUrl', message: 'Адрес OData: только https://…' })
  else if (/[?#]/.test(baseUrl) || /^https:\/\/[^/]*@/i.test(baseUrl))
    issues.push({ field: 'baseUrl', message: 'Адрес OData: без логина в адресе, параметров и якоря' })
  else if (canonicalUrl(baseUrl) !== baseUrl)
    issues.push({ field: 'baseUrl', message: `Адрес OData: сервер примет только вид ${canonicalUrl(baseUrl) ?? 'https://…'}` })
  checkText(issues, settings, 'infobase', 'Информационная база')
  checkText(issues, settings, 'username', 'Пользователь 1С')
  const profile = settings.configurationProfile
  if (!ONEC_PROFILES.some((item) => item.value === profile))
    issues.push({ field: 'configurationProfile', message: 'Конфигурация: выберите из списка' })
  const template = settings.creationTemplate
  if (template !== undefined) {
    const values = typeof template === 'object' && template !== null ? (template as Record<string, unknown>) : {}
    for (const { key, label } of ONEC_TEMPLATE_KEYS) {
      const value = values[key]
      if (typeof value !== 'string' || !GUID.test(value) || value === EMPTY_GUID)
        issues.push({ field: `creationTemplate.${key}`, message: `${label}: ссылка 1С (GUID)` })
    }
  }
  const defaults = settings.creationDefaults
  if (defaults !== undefined) {
    const values = typeof defaults === 'object' && defaults !== null ? (defaults as Record<string, unknown>) : {}
    for (const { key, label } of ONEC_CREATION_DEFAULTS) {
      const value = values[key]
      if (value === undefined) continue
      if (typeof value !== 'string' || value === '' || value.length > DEFAULT_MAX_LENGTH || value.trim() !== value)
        issues.push({
          field: `creationDefaults.${key}`,
          message: `${label}: непустое значение не длиннее ${DEFAULT_MAX_LENGTH} знаков, без пробелов по краям`,
        })
    }
  }
  return issues
}
